import {
  type Checklist as ChecklistProto,
  GetChecklistRequest,
  ProvideSupportingItemBusinessInfoRequest,
  ProvideSupportingItemBusinessRefundMethodRequest,
  ProvideSupportingItemDependentInfoRequest,
  ProvideSupportingItemNotesRequest,
  ProvideSupportingItemPersonalInfoRequest,
  ProvideSupportingItemPersonalRefundMethodRequest,
  ProvideSupportingItemSpousalInfoRequest,
  RemoveSupportingItemDependentInfoRequest,
  type RequestedSupportingItem as RequestedSupportingItemProto,
  type RequestedSupportingItemUpdatedRealtimeEvent,
  type Section as SectionProto,
  SupportingItemInput as SupportingItemInputProto,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import type { DocumentRequestSubmittedRealtimeEvent } from '@taxfyle/api-internal/internal/document_request_pb'
import { action } from 'mobx'
import { map, tap } from 'rxjs'
import { orderBy } from 'lodash-es'
import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import {
  onRpcNotFoundReturnNull,
  safeTimestampToDate,
  timestampToDate,
} from '@/utils/grpc-util'
import { isNotNil } from '@/utils/object-util'
import { filterNonNil } from '@/utils/rx-util'
import type {
  PrimeDocumentFn,
  PrimeDocumentTypeByShortIdFn,
} from '@/features/documents'
import { DocumentType } from '@/features/documents/DocumentType'
import { type JobsApi } from '@/features/jobs/api/job-api'
import {
  Checklist,
  type ChecklistProps,
  type ChecklistSectionProps,
} from './Checklist'
import type { RequestedSupportingItemProps } from './RequestedSupportingItem'
import type { SupportingItemInputProps } from './supporting-item-input'
import { DependentsInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/DependentsInfoSupportingItemInput'
import {
  mapProvidedProfileProtoToProps,
  mapProvidedTaxRefundMethodProtoToProps,
} from './info-gathering-mappers'

/**
 * Primes the documents referenced by document supporting items.
 */
interface DocumentPrimes {
  primeDocument: PrimeDocumentFn
  primeDocumentType: PrimeDocumentTypeByShortIdFn
}

/**
 * Manages the info gathering checklists of jobs.
 */
export class InfoGatheringStore {
  readonly checklists = collection({
    create: () => new Checklist(),
    getModelId: (m) => m.jobId,
    getDataId: (d) => d.jobId,
  })

  /**
   * Resolved by the first checklist fetch; realtime updates only apply to
   * checklists that have been fetched.
   */
  private _documentPrimes: DocumentPrimes | null = null

  constructor(
    private readonly _api: JobsApi,
    private readonly _primeDocument: PromiseLike<PrimeDocumentFn>,
    private readonly _primeDocumentType: PromiseLike<PrimeDocumentTypeByShortIdFn>,
  ) {
    this._api.realtime$
      .pipe(
        map((e) => e.getRequestedSupportingItemUpdated()),
        filterNonNil(),
        tap(this._onRemoteItemUpdated),
      )
      .subscribe()

    this._api.realtime$
      .pipe(
        map((e) => e.getDocumentRequestCreated()),
        filterNonNil(),
        tap(this._onRemoteDocumentRequestCreated),
      )
      .subscribe()
  }

  /**
   * Fetches the checklist for a job.
   */
  async fetchChecklist(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<Checklist | null> {
    const id = Id.assert(jobId)
    const response = await this._api.infoGathering
      .getChecklist(new GetChecklistRequest().setJobId(id))
      .catch(onRpcNotFoundReturnNull)
    const primes = await this._resolveDocumentPrimes()
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.checklists.set(
      this._mapChecklistProtoToProps(id, response, primes),
    )
  }

  /**
   * Provides notes to a supporting item.
   */
  async provideSupportingItemNotes({
    jobId,
    itemId,
    notes,
  }: ProvideNotesOptions) {
    const request = new ProvideSupportingItemNotesRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setNotes(notes)
    await this._api.infoGathering.provideSupportingItemNotes(request)
    await this.fetchChecklist(jobId)
  }

  /**
   * Provides a personal profile version to a supporting item.
   */
  async provideSupportingItemPersonalInfo(options: ProvideProfileOptions) {
    const request = new ProvideSupportingItemPersonalInfoRequest()
      .setJobId(Id.assert(options.jobId))
      .setId(Id.assert(options.itemId))
      .setProfileId(Id.assert(options.profileId))
      .setProfileVersion(options.profileVersion)
    await this._api.infoGathering.provideSupportingItemPersonalInfo(request)
    await this.fetchChecklist(options.jobId)
  }

  /**
   * Provides a spousal profile version to a supporting item.
   */
  async provideSupportingItemSpousalInfo(options: ProvideProfileOptions) {
    const request = new ProvideSupportingItemSpousalInfoRequest()
      .setJobId(Id.assert(options.jobId))
      .setId(Id.assert(options.itemId))
      .setProfileId(Id.assert(options.profileId))
      .setProfileVersion(options.profileVersion)
    await this._api.infoGathering.provideSupportingItemSpousalInfo(request)
    await this.fetchChecklist(options.jobId)
  }

  /**
   * Provides a business profile version to a supporting item.
   */
  async provideSupportingItemBusinessInfo(options: ProvideProfileOptions) {
    const request = new ProvideSupportingItemBusinessInfoRequest()
      .setJobId(Id.assert(options.jobId))
      .setId(Id.assert(options.itemId))
      .setProfileId(Id.assert(options.profileId))
      .setProfileVersion(options.profileVersion)
    await this._api.infoGathering.provideSupportingItemBusinessInfo(request)
    await this.fetchChecklist(options.jobId)
  }

  /**
   * Provides a personal tax refund method version to a supporting item.
   */
  async provideSupportingItemPersonalRefundMethod(
    options: ProvideTaxRefundMethodOptions,
  ) {
    const request = new ProvideSupportingItemPersonalRefundMethodRequest()
      .setJobId(Id.assert(options.jobId))
      .setId(Id.assert(options.itemId))
      .setRefundMethodId(Id.assert(options.refundMethodId))
      .setRefundMethodVersion(options.refundMethodVersion)
    await this._api.infoGathering.provideSupportingItemPersonalRefundMethod(
      request,
    )
    await this.fetchChecklist(options.jobId)
  }

  /**
   * Provides a business tax refund method version to a supporting item.
   */
  async provideSupportingItemBusinessRefundMethod(
    options: ProvideTaxRefundMethodOptions,
  ) {
    const request = new ProvideSupportingItemBusinessRefundMethodRequest()
      .setJobId(Id.assert(options.jobId))
      .setId(Id.assert(options.itemId))
      .setRefundMethodId(Id.assert(options.refundMethodId))
      .setRefundMethodVersion(options.refundMethodVersion)
    await this._api.infoGathering.provideSupportingItemBusinessRefundMethod(
      request,
    )
    await this.fetchChecklist(options.jobId)
  }

  /**
   * Adds a dependent profile version to a supporting item.
   */
  async provideSupportingItemDependentInfo(options: ProvideProfileOptions) {
    const request = new ProvideSupportingItemDependentInfoRequest()
      .setJobId(Id.assert(options.jobId))
      .setId(Id.assert(options.itemId))
      .setProfileId(Id.assert(options.profileId))
      .setProfileVersion(options.profileVersion)
    await this._api.infoGathering.provideSupportingItemDependentInfo(request)
    this._getDependentsInfoInput(options.jobId, options.itemId)?.addDependent({
      profileId: options.profileId,
      profileVersion: options.profileVersion,
    })
    void this.fetchChecklist(options.jobId).catch(() => undefined)
  }

  /**
   * Removes a dependent from a supporting item.
   */
  async removeSupportingItemDependentInfo({
    jobId,
    itemId,
    profileId,
  }: RemoveDependentOptions) {
    const request = new RemoveSupportingItemDependentInfoRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setProfileId(Id.assert(profileId))
    await this._api.infoGathering.removeSupportingItemDependentInfo(request)
    this._getDependentsInfoInput(jobId, itemId)?.removeDependent(profileId)
    void this.fetchChecklist(jobId).catch(() => undefined)
  }

  private _getDependentsInfoInput(jobId: Id, itemId: Id) {
    const input = this.checklists.get(jobId)?.getItem(itemId)?.input
    return input instanceof DependentsInfoSupportingItemInput ? input : null
  }

  private async _resolveDocumentPrimes(): Promise<DocumentPrimes> {
    const [primeDocument, primeDocumentType] = await Promise.all([
      this._primeDocument,
      this._primeDocumentType,
    ])
    this._documentPrimes = { primeDocument, primeDocumentType }
    return this._documentPrimes
  }

  /**
   * Applies a remote update to a requested supporting item, unless we
   * already have a more recent version of it.
   */
  @action.bound
  private _onRemoteItemUpdated(
    event: RequestedSupportingItemUpdatedRealtimeEvent,
  ) {
    const proto = event.getRequestedSupportingItem()
    const primes = this._documentPrimes
    if (!proto || !primes) {
      return
    }

    const item = this.checklists.get(event.getJobId())?.getItem(proto.getId())
    if (!item) {
      return
    }

    const updatedAt = timestampToDate(proto.getUpdateTime())
    if (updatedAt && item.updatedAt < updatedAt) {
      item.set(this._mapRequestedSupportingItemProtoToProps(proto, primes))
    }
  }

  /**
   * A new document request may add a section, so the checklist is reloaded.
   */
  @action.bound
  private _onRemoteDocumentRequestCreated(
    event: DocumentRequestSubmittedRealtimeEvent,
  ) {
    const jobId = event.getJobId()
    if (!this.checklists.get(jobId)) {
      return
    }

    void this.fetchChecklist(jobId).catch(() => undefined)
  }

  private _mapChecklistProtoToProps(
    jobId: Id,
    proto: ChecklistProto,
    primes: DocumentPrimes,
  ): ChecklistProps {
    return {
      jobId,
      sections: proto
        .getSectionsList()
        .map((section) => this._mapSectionProtoToProps(section, primes)),
    }
  }

  private _mapSectionProtoToProps(
    proto: SectionProto,
    primes: DocumentPrimes,
  ): ChecklistSectionProps {
    return {
      id: Id(proto.getId()),
      name: proto.getName(),
      heading: proto.getHeading(),
      description: proto.getDescription(),
      requestedSupportingItems: orderBy(
        proto.getRequestedSupportingItemsList(),
        (item) => item.getRequired(),
        'desc',
      ).map((item) =>
        this._mapRequestedSupportingItemProtoToProps(item, primes),
      ),
    }
  }

  private _mapRequestedSupportingItemProtoToProps(
    proto: RequestedSupportingItemProto,
    primes: DocumentPrimes,
  ): RequestedSupportingItemProps {
    const inputProto = proto.getInput()
    return {
      id: Id(proto.getId()),
      name: proto.getName(),
      heading: proto.getHeading(),
      shortDescription: proto.getShortDescription(),
      longDescription: proto.getLongDescription(),
      imageUrl: proto.getImageUrl()?.getValue() ?? null,
      previewUrl: proto.getPreviewUrl()?.getValue() ?? null,
      required: proto.getRequired(),
      adHoc: proto.getAdHoc(),
      completed: proto.getCompleted(),
      updatedAt: safeTimestampToDate(proto.getUpdateTime()),
      input: inputProto
        ? this._mapSupportingItemInputProtoToProps(inputProto, primes)
        : { type: 'Notes', text: '' },
    }
  }

  private _mapSupportingItemInputProtoToProps(
    proto: SupportingItemInputProto,
    primes: DocumentPrimes,
  ): SupportingItemInputProps {
    switch (proto.getTypeCase()) {
      case SupportingItemInputProto.TypeCase.DOCUMENT: {
        const input = proto.getDocument()!
        const documentTypeShortId = input.getDocumentTypeShortId()
        return {
          type: 'Document',
          documentType:
            (documentTypeShortId &&
              primes.primeDocumentType(documentTypeShortId)) ||
            DocumentType.none,
          documents: input
            .getDocumentLongIdsList()
            .map((longId) => primes.primeDocument({ id: longId }))
            .filter(isNotNil),
        }
      }
      case SupportingItemInputProto.TypeCase.PERSONAL_INFO: {
        return {
          type: 'PersonalInfo',
          provided: mapProvidedProfileProtoToProps(proto.getPersonalInfo()!),
        }
      }
      case SupportingItemInputProto.TypeCase.SPOUSAL_INFO: {
        return {
          type: 'SpousalInfo',
          provided: mapProvidedProfileProtoToProps(proto.getSpousalInfo()!),
        }
      }
      case SupportingItemInputProto.TypeCase.DEPENDENTS_INFO: {
        const input = proto.getDependentsInfo()!
        return {
          type: 'DependentsInfo',
          dependents: input.getDependentsList().map((dependent) => ({
            profileId: Id(dependent.getProfileId()?.getValue()),
            profileVersion: dependent.getProfileVersion()?.getValue() ?? 0,
          })),
        }
      }
      case SupportingItemInputProto.TypeCase.PERSONAL_REFUND_METHOD: {
        return {
          type: 'PersonalRefundMethodInfo',
          provided: mapProvidedTaxRefundMethodProtoToProps(
            proto.getPersonalRefundMethod()!,
          ),
        }
      }
      case SupportingItemInputProto.TypeCase.BUSINESS_INFO: {
        return {
          type: 'BusinessInfo',
          provided: mapProvidedProfileProtoToProps(proto.getBusinessInfo()!),
        }
      }
      case SupportingItemInputProto.TypeCase.BUSINESS_REFUND_METHOD: {
        return {
          type: 'BusinessRefundMethodInfo',
          provided: mapProvidedTaxRefundMethodProtoToProps(
            proto.getBusinessRefundMethod()!,
          ),
        }
      }
      // Notes is the simplest input, so unknown types fall back to it.
      case SupportingItemInputProto.TypeCase.NOTES:
      case SupportingItemInputProto.TypeCase.TYPE_NOT_SET:
      default:
        return {
          type: 'Notes',
          text: proto.getNotes()?.getText()?.getValue() ?? '',
        }
    }
  }
}

/**
 * Options for providing notes to a supporting item.
 */
export interface ProvideNotesOptions {
  jobId: Id
  itemId: Id
  notes: string
}

/**
 * Options for providing a profile version to a supporting item.
 */
export interface ProvideProfileOptions {
  jobId: Id
  itemId: Id
  profileId: Id
  profileVersion: number
}

/**
 * Options for providing a tax refund method version to a supporting item.
 */
export interface ProvideTaxRefundMethodOptions {
  jobId: Id
  itemId: Id
  refundMethodId: Id
  refundMethodVersion: number
}

/**
 * Options for removing a dependent from a supporting item.
 */
export interface RemoveDependentOptions {
  jobId: Id
  itemId: Id
  profileId: Id
}
